import { useState, useCallback } from 'react';
import { AnimatedSection } from '../AnimatedSection/AnimatedSection';
import { Lightbox } from '../Lightbox/Lightbox';
import './style.less';

export const ProjectShowcase = ({
  id,
  title,
  style,
  location,
  description,
  mainImage,
  gallery = [],
  reversed = false,
  index = 0,
}) => {
  const displayGallery = gallery.slice(0, 4);
  const allImages = [mainImage, ...displayGallery];

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openLightbox = useCallback((imageIndex) => {
    setCurrentImageIndex(imageIndex);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const goToNext = useCallback(
    (specificIndex) => {
      if (typeof specificIndex === 'number') {
        setCurrentImageIndex(specificIndex);
      } else {
        setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
      }
    },
    [allImages.length]
  );

  const goToPrev = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  }, [allImages.length]);

  const handleKeyDown = (e, imageIndex) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openLightbox(imageIndex);
    }
  };

  return (
    <div id={id} className={`project-showcase ${reversed ? 'reversed' : ''}`}>
      <div className="project-showcase-inner">
        {/* Image Gallery Side */}
        <AnimatedSection className="project-gallery" delay={0.1}>
          <div
            className="gallery-main"
            onClick={() => openLightbox(0)}
            onKeyDown={(e) => handleKeyDown(e, 0)}
            role="button"
            tabIndex={0}
            aria-label={`View ${title} main image in fullscreen`}
          >
            <img src={mainImage} alt={title} loading="lazy" />
            <div className="gallery-overlay" aria-hidden="true"></div>
          </div>
          <div className="gallery-grid">
            {displayGallery.map((img, idx) => (
              <div
                key={idx}
                className="gallery-item"
                onClick={() => openLightbox(idx + 1)}
                onKeyDown={(e) => handleKeyDown(e, idx + 1)}
                role="button"
                tabIndex={0}
                aria-label={`View ${title} image ${idx + 2} in fullscreen`}
              >
                <img src={img} alt={`${title} - ${idx + 1}`} loading="lazy" />
              </div>
            ))}
          </div>
        </AnimatedSection>

        <Lightbox
          images={allImages}
          currentIndex={currentImageIndex}
          isOpen={lightboxOpen}
          onClose={closeLightbox}
          onNext={goToNext}
          onPrev={goToPrev}
        />

        {/* Project Info Side */}
        <div className="project-info">
          <AnimatedSection delay={0.2}>
            <span className="project-number">0{index + 1}</span>
          </AnimatedSection>
          <AnimatedSection delay={0.3}>
            <h3 className="project-title">{title}</h3>
          </AnimatedSection>
          <AnimatedSection delay={0.4}>
            <div className="project-meta">
              <div className="meta-item">
                <i className="fas fa-palette" aria-hidden="true"></i>
                <span>{style}</span>
              </div>
              <div className="meta-item">
                <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
                <span>{location}</span>
              </div>
            </div>
          </AnimatedSection>
          {description && (
            <AnimatedSection delay={0.5}>
              <p className="project-description">{description}</p>
            </AnimatedSection>
          )}
          <AnimatedSection delay={0.6}>
            <div className="project-decorative">
              <div className="decorative-line" aria-hidden="true"></div>
              <div className="decorative-dot" aria-hidden="true"></div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </div>
  );
};
